import React, { forwardRef, ReactNode, useState } from 'react';
import { ButtonBase, IconButton, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { X } from 'react-feather';
import { AssistantTypingDots } from 'src/modules/assistant/components/AssistantTypingDots';
import {
    ASSISTANT_CHAT_REGION_ID,
    ASSISTANT_NAME,
    GREETING_PHASE,
    TGreetingPhase,
} from 'src/modules/assistant/constants/widget';
import { useLauncherStyles } from './styles';

const CLOSE_ICON_SIZE = 18;

interface AssistantLauncherProps {
    open: boolean;
    busy: boolean;
    responseReady: boolean;
    listening: boolean;
    greeting: {
        phase: TGreetingPhase;
        message: string;
        writtenMessage: string;
    };
    onOpen: () => void;
    onClose: () => void;
}

/**
 * Header of the widget. Closed it is only the orb, with the status in a tooltip that
 * does not take clicks, so it never covers page controls. Open it shows the name
 * and the close button.
 */
export const AssistantLauncher = forwardRef<
    HTMLButtonElement,
    AssistantLauncherProps
>(
    (
        { open, busy, responseReady, listening, greeting, onOpen, onClose },
        ref
    ) => {
        const classes = useLauncherStyles();
        const { t } = useTranslation();
        const closeLabel = t('Assistant.Close', { name: ASSISTANT_NAME });
        const [hovered, setHovered] = useState(false);
        const announcing =
            busy || responseReady || greeting.phase !== GREETING_PHASE.HIDDEN;

        const renderStatus = (): ReactNode => {
            if (open) return ASSISTANT_NAME;
            if (busy) return t('Assistant.Analyzing');
            if (responseReady) return t('Assistant.ResponseReady');
            if (greeting.phase === GREETING_PHASE.TYPING)
                return <AssistantTypingDots />;
            if (greeting.phase === GREETING_PHASE.WRITING)
                return (
                    <>
                        {greeting.writtenMessage}
                        <span
                            className={classes.typingCaret}
                            aria-hidden="true"
                        />
                    </>
                );
            if (greeting.phase === GREETING_PHASE.MESSAGE)
                return greeting.message;
            return ASSISTANT_NAME;
        };

        return (
            <div
                className={`${classes.header} ${
                    open ? classes.headerOpen : ''
                }`}
            >
                <Tooltip
                    classes={{ popper: classes.statusPopper }}
                    onClose={() => setHovered(false)}
                    onOpen={() => setHovered(true)}
                    open={!open && (hovered || announcing)}
                    placement="left"
                    title={renderStatus()}
                >
                    <ButtonBase
                        aria-controls={
                            open ? ASSISTANT_CHAT_REGION_ID : undefined
                        }
                        aria-expanded={open}
                        aria-label={
                            open
                                ? ASSISTANT_NAME
                                : t('Assistant.Open', { name: ASSISTANT_NAME })
                        }
                        className={`${classes.launcher} ${
                            open ? '' : classes.launcherCollapsed
                        }`}
                        onClick={onOpen}
                        ref={ref}
                    >
                        <span
                            aria-live="polite"
                            className={
                                open
                                    ? classes.launcherText
                                    : classes.launcherTextHidden
                            }
                        >
                            {renderStatus()}
                        </span>
                        <span
                            aria-hidden="true"
                            className={`${classes.orb} ${
                                responseReady && !open ? classes.orbReady : ''
                            } ${listening ? classes.orbListening : ''}`}
                        />
                    </ButtonBase>
                </Tooltip>
                {open && (
                    <Tooltip title={closeLabel}>
                        <IconButton
                            aria-label={closeLabel}
                            className={classes.closeButton}
                            onClick={onClose}
                            size="small"
                        >
                            <X aria-hidden="true" size={CLOSE_ICON_SIZE} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        );
    }
);

AssistantLauncher.displayName = 'AssistantLauncher';
